import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';

/** One set of slider values, as whole percents. */
export interface PosSize {
  textPct: number;
  buttonPct: number;
  /**
   * The left order panel's text size, independent of the menu/buttons Text size. Null / absent
   * = follows [textPct] (the default, so nothing changes until someone moves it).
   */
  summaryPct?: number | null;
}

/** Where the value in force came from: this terminal, the store, or the legacy presets. */
export type PosSizeSource = 'device' | 'store' | 'legacy';

/**
 * POS Text size + Button size (ENG-5842) — two independent, continuous sliders.
 *
 * Text size (80–150 %) multiplies every POS font size and line height (`--pos-text-scale`;
 * sizes of 24px and up use `--pos-text-display-scale` = t^0.6 so the big numbers grow less).
 * Button size (80–140 %) multiplies every box: tile heights, touch targets, padding, gaps,
 * dialog widths (`--pos-scale`, the variable the POS has always used for that). The menu
 * grid's minimum tile width and target column count are derived from both
 * (`--pos-grid-min-cell`, `--pos-grid-cols`).
 *
 * A third slider, Order summary text (80–150 %), sizes only the left order panel:
 * `--pos-summary-text-scale`, which `app-order-summary` maps onto `--pos-text-scale` for
 * everything inside it (styles.scss). Unset, it follows the Text size.
 *
 * Value in force: this terminal's override (localStorage) → the store default
 * (`store_data.pos_text_pct` / `pos_button_pct`) → derived from the legacy presets
 * (`pos_ui_scale`, `pos_menu_text_size`) for stores that never touched the sliders, so
 * nothing changes until someone moves one.
 *
 * This is the only writer of those CSS variables. PosUiScaleService and
 * PosMenuTextSizeService remain as the legacy preset holders and report into here.
 * The native POS does the same in PosSize.kt.
 */
@Injectable({providedIn: 'root'})
export class PosSizeService {

  static readonly TEXT_RANGE: [number, number] = [80, 150];
  static readonly BUTTON_RANGE: [number, number] = [80, 140];
  static readonly STEP = 5;

  static readonly DEVICE_TEXT_KEY = 'posTextPctDevice';
  static readonly DEVICE_BUTTON_KEY = 'posButtonPctDevice';
  static readonly DEVICE_SUMMARY_KEY = 'posSummaryTextPctDevice';
  static readonly STORE_CACHE_KEY = 'posSizeStore';

  /** Menu tile label size at 100 %, the minimum tile width and padding — PosMenuGrid on the APK. */
  static readonly LABEL_PX = 15;
  static readonly MIN_CELL_PX = 88;
  static readonly CELL_PAD_PX = 12;
  /** "Appetizers" in Roboto Bold: a tile is never so narrow a common long word must shrink. */
  static readonly LONG_WORD_EM = 4.9;

  private legacyScale = 1;
  private legacyMenuPx = PosSizeService.LABEL_PX;
  private storeSize: Partial<PosSize> = {};
  private deviceSize: Partial<PosSize> = {};
  private previewSize: PosSize | null = null;
  private phoneFactor = 1;

  private currentSubject = new BehaviorSubject<PosSize>({textPct: 100, buttonPct: 100});

  constructor() {
    this.deviceSize = {
      textPct: PosSizeService.readPct(PosSizeService.DEVICE_TEXT_KEY, PosSizeService.TEXT_RANGE),
      buttonPct: PosSizeService.readPct(PosSizeService.DEVICE_BUTTON_KEY, PosSizeService.BUTTON_RANGE),
      summaryPct: PosSizeService.readPct(PosSizeService.DEVICE_SUMMARY_KEY, PosSizeService.TEXT_RANGE),
    };
    try {
      const cached = JSON.parse(localStorage.getItem(PosSizeService.STORE_CACHE_KEY) || '{}');
      this.storeSize = {
        textPct: PosSizeService.clampPct(cached.textPct, PosSizeService.TEXT_RANGE),
        buttonPct: PosSizeService.clampPct(cached.buttonPct, PosSizeService.BUTTON_RANGE),
        summaryPct: PosSizeService.clampPct(cached.summaryPct, PosSizeService.TEXT_RANGE),
      };
    } catch {
      this.storeSize = {};
    }
  }

  /** The values in force on this terminal (a live preview included). */
  get current(): PosSize {
    return this.currentSubject.value;
  }

  get current$(): Observable<PosSize> {
    return this.currentSubject.asObservable();
  }

  /** The store default, derived from the legacy presets when the store never set one. */
  get storeDefault(): PosSize {
    const legacy = this.legacySize();
    return {
      textPct: this.storeSize.textPct ?? legacy.textPct,
      buttonPct: this.storeSize.buttonPct ?? legacy.buttonPct,
      summaryPct: this.storeSize.summaryPct ?? null,
    };
  }

  /** This terminal's own override, or null where it follows the store. */
  get deviceOverride(): Partial<PosSize> {
    return {...this.deviceSize};
  }

  get hasDeviceOverride(): boolean {
    return this.deviceSize.textPct != null || this.deviceSize.buttonPct != null || this.deviceSize.summaryPct != null;
  }

  source(key: keyof PosSize): PosSizeSource {
    if (this.deviceSize[key] != null) return 'device';
    return this.storeSize[key] != null ? 'store' : 'legacy';
  }

  /** Called once at app start (after the legacy services restored their cached presets). */
  init(): void {
    this.apply();
  }

  /** Mirrors the store payload's slider fields (null = the store never chose one). */
  syncFromStore(textPct: number | null | undefined, buttonPct: number | null | undefined,
                summaryPct?: number | null): void {
    const next = {
      textPct: PosSizeService.clampPct(textPct, PosSizeService.TEXT_RANGE),
      buttonPct: PosSizeService.clampPct(buttonPct, PosSizeService.BUTTON_RANGE),
      summaryPct: PosSizeService.clampPct(summaryPct, PosSizeService.TEXT_RANGE),
    };
    if (next.textPct === this.storeSize.textPct && next.buttonPct === this.storeSize.buttonPct &&
        next.summaryPct === this.storeSize.summaryPct) return;
    this.storeSize = next;
    try {
      localStorage.setItem(PosSizeService.STORE_CACHE_KEY, JSON.stringify(next));
    } catch { /* private mode: the store payload re-applies it on every load */ }
    this.apply();
  }

  /** Sets the store default locally, ahead of the confirming store reload. */
  setStoreDefault(size: PosSize): void {
    this.syncFromStore(size.textPct, size.buttonPct, size.summaryPct);
  }

  /** Sets (or with null, clears) this terminal's override. */
  setDeviceOverride(size: Partial<PosSize> | null): void {
    this.deviceSize = {
      textPct: PosSizeService.clampPct(size?.textPct, PosSizeService.TEXT_RANGE),
      buttonPct: PosSizeService.clampPct(size?.buttonPct, PosSizeService.BUTTON_RANGE),
      summaryPct: PosSizeService.clampPct(size?.summaryPct, PosSizeService.TEXT_RANGE),
    };
    PosSizeService.writePct(PosSizeService.DEVICE_TEXT_KEY, this.deviceSize.textPct);
    PosSizeService.writePct(PosSizeService.DEVICE_BUTTON_KEY, this.deviceSize.buttonPct);
    PosSizeService.writePct(PosSizeService.DEVICE_SUMMARY_KEY, this.deviceSize.summaryPct);
    this.apply();
  }

  /** Shows a size live without saving it (the picker, while a slider moves). Null ends it. */
  preview(size: PosSize | null): void {
    this.previewSize = size;
    this.apply();
  }

  /** Legacy presets, reported by PosUiScaleService / PosMenuTextSizeService. */
  setLegacy(legacy: { scale?: number; menuPx?: number }): void {
    if (legacy.scale != null) this.legacyScale = legacy.scale;
    if (legacy.menuPx != null) this.legacyMenuPx = legacy.menuPx;
    this.apply();
  }

  /** Extra multiplier while the Orders module shows its phone layout (see PosUiScaleService). */
  setPhoneFactor(factor: number): void {
    const f = factor > 0 ? factor : 1;
    if (f === this.phoneFactor) return;
    this.phoneFactor = f;
    this.apply();
  }

  /**
   * The CSS variables for a size, applied to <html> by [apply] and to the picker's preview
   * frame, so the preview and the POS can never disagree.
   */
  static cssVars(size: PosSize, phoneFactor = 1): { [name: string]: string } {
    const t = size.textPct / 100 * phoneFactor;
    const b = size.buttonPct / 100 * phoneFactor;
    const ts = (size.summaryPct ?? size.textPct) / 100 * phoneFactor;
    const round = (n: number) => String(Math.round(n * 1000) / 1000);
    const label = PosSizeService.LABEL_PX * t;
    const minCell = Math.max(
      PosSizeService.MIN_CELL_PX * b,
      PosSizeService.LONG_WORD_EM * label + 2 * PosSizeService.CELL_PAD_PX * b,
    );
    return {
      '--pos-scale': round(b),
      '--pos-text-scale': round(t),
      '--pos-text-display-scale': round(Math.pow(t, 0.6)),
      '--pos-summary-text-scale': round(ts),
      '--pos-summary-text-display-scale': round(Math.pow(ts, 0.6)),
      '--pos-menu-text-size': `${round(label)}px`,
      '--pos-grid-min-cell': `${Math.round(minCell)}px`,
      '--pos-grid-cols': String(PosSizeService.targetColumns(size.buttonPct)),
    };
  }

  /** Six across at 100 %, eight at 80 %, four at 140 %; bigger text only removes columns. */
  static targetColumns(buttonPct: number): number {
    return Math.max(2, Math.min(8, Math.round(6 / (buttonPct / 100))));
  }

  /** Rounds to the slider step and clamps into range; null for anything not a number. */
  static clampPct(value: unknown, [low, high]: [number, number]): number | undefined {
    const n = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof n !== 'number' || !isFinite(n)) return undefined;
    const stepped = Math.round(n / PosSizeService.STEP) * PosSizeService.STEP;
    return Math.min(high, Math.max(low, stepped));
  }

  private legacySize(): PosSize {
    return {
      buttonPct: PosSizeService.clampPct(this.legacyScale * 100, PosSizeService.BUTTON_RANGE) ?? 100,
      textPct: PosSizeService.clampPct(
        this.legacyScale * this.legacyMenuPx / PosSizeService.LABEL_PX * 100, PosSizeService.TEXT_RANGE) ?? 100,
    };
  }

  private resolved(): PosSize {
    if (this.previewSize) return this.previewSize;
    const store = this.storeDefault;
    const textPct = this.deviceSize.textPct ?? store.textPct;
    return {
      textPct,
      buttonPct: this.deviceSize.buttonPct ?? store.buttonPct,
      // Follows the Text size in force unless this terminal or the store set its own.
      summaryPct: this.deviceSize.summaryPct ?? store.summaryPct ?? null,
    };
  }

  private apply(): void {
    const size = this.resolved();
    const vars = PosSizeService.cssVars(size, this.phoneFactor);
    const root = document.documentElement.style;
    Object.keys(vars).forEach(name => root.setProperty(name, vars[name]));
    const prev = this.currentSubject.value;
    if (prev.textPct !== size.textPct || prev.buttonPct !== size.buttonPct || prev.summaryPct !== size.summaryPct) {
      this.currentSubject.next(size);
    }
  }

  private static readPct(key: string, range: [number, number]): number | undefined {
    try {
      return PosSizeService.clampPct(localStorage.getItem(key), range);
    } catch {
      return undefined;
    }
  }

  private static writePct(key: string, value: number | undefined): void {
    try {
      if (value == null) localStorage.removeItem(key);
      else localStorage.setItem(key, String(value));
    } catch { /* private mode: the override lasts this session only */ }
  }
}
